// apps/web/src/components/Tour/TourProvider.tsx
//
// Welcome tour wrapper. Renders nothing visible. Mount it once near the root
// of the AppLayout chrome so it can attach driver.js to the sidebar, top nav,
// and the content area. On mount, if the current user has
// `tourCompletedAt = null` AND the `tour.enabled` setting is true (registry
// GET returns the canonical boolean in `value`), it queues a 600ms timeout
// and then drives the tour. Both `done` and `skip` call
// POST /profile/tour-completed so subsequent mounts are silent.

import { useEffect, useRef } from 'react';

import { driver, type DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';

import { useMarkTourCompleted, useTourConfig } from '@alamo/api';
import { useAuth } from '@alamo/auth';

const STEPS: DriveStep[] = [
  {
    element: '[data-tour="sidebar"]',
    popover: {
      title: 'Bem-vindo ao Alamo',
      description:
        'Este é o menu principal — encontre cotações, equipe, treinamentos e mais. Você pode recolhê-lo a qualquer momento.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour="dashboard"]',
    popover: {
      title: 'Painel de controle',
      description:
        'Seu centro de operações: cards de status, atalhos rápidos e indicadores. Volte sempre aqui.',
      side: 'bottom',
    },
  },
  {
    element: '[data-tour="referral"]',
    popover: {
      title: 'Sua indicação',
      description:
        'Compartilhe seu código de indicação para crescer sua rede. O link curto está pronto para colar no WhatsApp.',
      side: 'bottom',
    },
  },
  {
    element: '[data-tour="quote"]',
    popover: {
      title: 'Nova cotação',
      description:
        'Comece uma cotação de proteção veicular em segundos. Use o atalho do menu lateral ou o botão grande do dashboard.',
      side: 'left',
    },
  },
  {
    element: '[data-tour="challenge"]',
    popover: {
      title: 'Desafio de 7 dias',
      description:
        'Complete sete tarefas em sete dias e ganhe um badge. Acompanhe pela barra lateral do painel.',
      side: 'right',
    },
  },
  {
    element: '[data-tour="training"]',
    popover: {
      title: 'Treinamentos',
      description:
        'Vídeos guiados para você aprender o jeito Alamo de vender. Comece pelos módulos da categoria "Comece por aqui".',
      side: 'right',
    },
  },
];

/**
 * Mounts the welcome tour. Renders no DOM by itself. Place inside
 * AppLayout so the data-tour anchors exist when the tour starts.
 */
export function TourProvider(): null {
  const { user, refreshUser } = useAuth();
  const shouldLoadTourConfig = Boolean(user && !user.tourCompletedAt);
  const tourConfigQuery = useTourConfig(shouldLoadTourConfig);
  const markCompleted = useMarkTourCompleted();
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    if (!user) return;
    // Tour gates:
    //   1. User has not completed/skipped before.
    //   2. The authenticated-user config explicitly enables the tour.
    if (user.tourCompletedAt) return;
    const enabled = tourConfigQuery.data?.enabled === true;
    if (!enabled) return;
    if (tourConfigQuery.isLoading) return;

    firedRef.current = true;

    const driverObj = driver({
      showProgress: true,
      allowClose: true,
      nextBtnText: 'Próximo',
      prevBtnText: 'Voltar',
      doneBtnText: 'Concluir',
      progressText: '{{current}} de {{total}}',
      // Skip steps whose anchor is missing — avoids a broken tour when a
      // user lands on a page that doesn't render every data-tour attribute.
      steps: STEPS.filter((step) => {
        if (!step.element || typeof step.element !== 'string') return true;
        return document.querySelector(step.element) !== null;
      }),
      onDestroyed: () => {
        // Fires on both "Concluir" (last step) and ESC/X (close). Both
        // mark the tour as completed so it doesn't replay. AuthProvider
        // keeps `user` in useState (not TanStack Query), so we need to
        // explicitly `refreshUser()` after the mutation — otherwise
        // user.tourCompletedAt stays stale and the tour re-fires on the
        // next route change.
        markCompleted.mutate(undefined, {
          onSuccess: () => {
            void refreshUser();
          },
        });
      },
    });

    // 600ms gives lazy-loaded chrome (sidebar permissions, dashboard
    // widgets) time to mount their data-tour anchors before we query.
    const handle = window.setTimeout(() => {
      driverObj.drive();
    }, 600);

    return () => {
      window.clearTimeout(handle);
      driverObj.destroy();
    };
    // We intentionally depend on user.id rather than the full user object
    // so background /auth/me invalidations don't re-fire the tour.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.tourCompletedAt, tourConfigQuery.data, tourConfigQuery.isLoading]);

  return null;
}
